// Tiny reactive i18n: two dictionaries, typed keys, {param} interpolation.
// Language = explicit setting → browser language → English.

import { setSetting } from '$lib/db/settings';
import { SETTING_LANGUAGE } from '$lib/db/setting-keys';

import { en, type Dict } from './en';
import { pl } from './pl';

export type LanguageChoice = 'system' | 'en' | 'pl';

const state = $state({ choice: 'system' as LanguageChoice });

function systemLanguage(): 'en' | 'pl' {
  return navigator.language.toLowerCase().startsWith('pl') ? 'pl' : 'en';
}

export function currentLanguage(): 'en' | 'pl' {
  return state.choice === 'system' ? systemLanguage() : state.choice;
}

function dict(): Dict {
  return currentLanguage() === 'pl' ? pl : en;
}

export function t(key: keyof Dict, params?: Record<string, string | number>): string {
  let text: string = dict()[key];
  if (params !== undefined) {
    for (const [name, value] of Object.entries(params)) {
      text = text.replaceAll(`{${name}}`, String(value));
    }
  }
  return text;
}

export function languageChoice(): LanguageChoice {
  return state.choice;
}

export async function setLanguage(choice: LanguageChoice): Promise<void> {
  state.choice = choice;
  document.documentElement.lang = currentLanguage();
  await setSetting(SETTING_LANGUAGE, choice);
}

/** The language to speak before anything has been read from disk — the browser's
 *  own, which `systemLanguage()` already knows synchronously. Called first so the
 *  app can render immediately instead of waiting on IndexedDB for a preference
 *  that, for almost everyone, resolves to exactly this. */
export function applyDefaultLanguage(): void {
  document.documentElement.lang = currentLanguage();
}

/** Reconciles with the stored choice once the prefetch lands. `state.choice` is
 *  reactive, so a user who picked something other than their system language sees
 *  it swap in on its own — a re-render, not a reload. Anything unrecognised
 *  (missing row, unreadable database) leaves the default standing. */
export function adoptStoredLanguage(stored: unknown): void {
  if (stored === 'en' || stored === 'pl' || stored === 'system') {
    state.choice = stored;
  }
  document.documentElement.lang = currentLanguage();
}
